import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';

const InvoiceList = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [downloadDropdown, setDownloadDropdown] = useState(null);
  const [printDropdown, setPrintDropdown] = useState(null);

  useEffect(() => {
    fetchInvoices();
    fetchPartners();
    fetchCompanies();
    fetchProjects();
  }, [currentPage, searchTerm, statusFilter, partnerFilter, companyFilter, projectFilter]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setDownloadDropdown(null);
        setPrintDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (message.type === 'success' && message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(partnerFilter && { partnerId: partnerFilter }),
        ...(companyFilter && { companyId: companyFilter }),
        ...(projectFilter && { projectId: projectFilter })
      });

      const response = await fetch(`/api/finance/invoices?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      } else {
        throw new Error(t('invoiceList.failedToFetchInvoices'));
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setMessage({ type: 'error', text: t('invoiceList.failedToLoadInvoices') });
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      // For invoice list filtering, only show active partners in the filter dropdown
      const response = await fetch('/api/finance/partners?limit=100&activeOnly=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || []);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch('/api/finance/companies?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch('/api/projects/list', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('invoiceList.invoiceDeletedSuccessfully') });
        setShowDeleteModal(false);
        setInvoiceToDelete(null);
        fetchInvoices();
      } else {
        throw new Error(t('invoiceList.failedToDeleteInvoice'));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setMessage({ type: 'error', text: t('invoiceList.failedToDeleteInvoice') });
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('invoiceList.invoiceStatusUpdatedSuccessfully') });
        fetchInvoices();
      } else {
        throw new Error(t('invoiceList.failedToUpdateInvoiceStatus'));
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      setMessage({ type: 'error', text: t('invoiceList.failedToUpdateInvoiceStatus') });
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-gray-300 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Paid':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'Sent':
        return <ClockIcon className="h-4 w-4" />;
      case 'Partial':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'Overdue':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'Draft':
        return <DocumentTextIcon className="h-4 w-4" />;
      default:
        return <DocumentTextIcon className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount, currency = 'BGN') => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  // Calculate effective status (including overdue)
  const getEffectiveStatus = (invoice) => {
    if (invoice.status === 'Paid' || invoice.status === 'Draft' || invoice.status === 'Cancelled' || invoice.status === 'Partial') {
      return invoice.status;
    }
    if (invoice.status === 'Sent' && new Date(invoice.dueDate) < new Date()) {
      return 'Overdue';
    }
    return invoice.status;
  };

  const handleDownload = async (invoiceId, invoiceNumber, invoiceType = 'ORIGINAL') => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/invoices/${invoiceId}/pdf?type=${invoiceType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNumber}-${invoiceType}.pdf`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setMessage({ type: 'success', text: t('invoiceList.invoiceDownloadedSuccessfully', { type: invoiceType }) });
      } else {
        throw new Error(t('invoiceList.failedToDownloadInvoiceAsPDF'));
      }
    } catch (error) {
      console.error('Error downloading invoice as PDF:', error);
      setMessage({ type: 'error', text: t('invoiceList.failedToDownloadInvoiceAsPDF') });
    }
  };

  const handlePrint = async (invoiceId, invoiceNumber, invoiceType = 'ORIGINAL') => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/invoices/${invoiceId}/pdf?type=${invoiceType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Open PDF in new tab for preview and printing
        const newWindow = window.open(url, '_blank');
        
        // Clean up URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);
        
      } else {
        throw new Error(t('invoiceList.failedToLoadInvoiceForPrinting'));
      }
    } catch (error) {
      console.error('Error printing invoice:', error);
      setMessage({ type: 'error', text: t('invoiceList.failedToPrintInvoice') });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto">
      {/* Hero Section */}
      <div className="flex items-center justify-between mb-6 px-4 sm:px-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invoiceList.invoices')}</h1>
          <p className="text-gray-600">{t('invoiceList.manageInvoicesTrackPayments')}</p>
        </div>
        <button 
          onClick={() => window.location.href = '/admin/dashboard/invoices/create'}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <PlusIcon className="h-4 w-4" />
          <span>{t('invoiceList.newInvoice')}</span>
        </button>
      </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <span>{message.text}</span>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 mx-4 sm:mx-6 lg:mx-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('invoiceList.searchInvoicesItemsNotes')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('invoiceList.allStatus')}</option>
                <option value="Draft">{t('invoiceList.draft')}</option>
                <option value="Sent">{t('invoiceList.sent')}</option>
                <option value="Partial">{t('invoiceList.partial')}</option>
                <option value="Paid">{t('invoiceList.paid')}</option>
                <option value="Overdue">{t('invoiceList.overdue')}</option>
                <option value="Cancelled">{t('invoiceList.cancelled')}</option>
              </select>
            </div>

            <div>
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('invoiceList.allPartners')}</option>
                {partners.map((partner) => (
                  <option key={partner._id} value={partner._id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('invoiceList.allCompanies')}</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('invoiceList.allProjects')}</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>


          </div>
        </div>

        {/* Invoices List */}
        <div className="bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600">{t('invoiceList.loadingInvoices')}</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('invoiceList.noInvoicesFound')}</p>
              <button 
                onClick={() => window.location.href = '/admin/dashboard/invoices/create'}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                {t('invoiceList.createFirstInvoice')}
              </button>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <span className="text-sm text-gray-600">
                  {total === 1 ? t('invoiceList.oneInvoiceFound') : t('invoiceList.multipleInvoicesFound', { count: total })}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1400px' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                        {t('invoiceList.invoice')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%]">
                        {t('invoiceList.partner')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                        {t('invoiceList.project')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                        {t('invoiceList.amount')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                        {t('invoiceList.status')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                        {t('invoiceList.issueDate')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                        {t('invoiceList.dueDate')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">
                        {t('invoiceList.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <tr key={invoice._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </div>
                            <div className="text-sm text-gray-500">
                              {invoice.companyId?.name || t('invoiceList.unknownCompany')}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-900">
                              {invoice.partnerId?.name || t('invoiceList.unknownPartner')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {invoice.partnerId?.email || ''}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-900">
                              {invoice.projectId?.name || t('invoiceList.noProject')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {invoice.projectId?.status || ''}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(invoice.totalAmount, invoice.currency)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {t('invoiceList.itemsCount', { count: invoice.items?.length || 0 })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(getEffectiveStatus(invoice))}
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getEffectiveStatus(invoice))}`}>
                                {t(`invoiceList.status${getEffectiveStatus(invoice)}`)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(invoice.issueDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm ${isOverdue(invoice.dueDate) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                            {formatDate(invoice.dueDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button 
                              onClick={() => window.location.href = `/admin/dashboard/invoices/${invoice._id}`}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <div className="relative">
                              <button 
                                onClick={() => setDownloadDropdown(downloadDropdown === invoice._id ? null : invoice._id)}
                                className="text-green-600 hover:text-green-900"
                                title="Download PDF"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                              {downloadDropdown === invoice._id && (
                                <div 
                                  className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200"
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handleDownload(invoice._id, invoice.invoiceNumber, 'ORIGINAL');
                                        setDownloadDropdown(null);
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('invoiceList.downloadOriginal')}
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleDownload(invoice._id, invoice.invoiceNumber, 'COPY');
                                        setDownloadDropdown(null);
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('invoiceList.downloadCopy')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative">
                              <button 
                                onClick={() => setPrintDropdown(printDropdown === invoice._id ? null : invoice._id)}
                                className="text-purple-600 hover:text-purple-900"
                                title="Print PDF"
                              >
                                <PrinterIcon className="h-4 w-4" />
                              </button>
                              {printDropdown === invoice._id && (
                                <div 
                                  className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200"
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handlePrint(invoice._id, invoice.invoiceNumber, 'ORIGINAL');
                                        setPrintDropdown(null);
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('invoiceList.printOriginal')}
                                    </button>
                                    <button
                                      onClick={() => {
                                        handlePrint(invoice._id, invoice.invoiceNumber, 'COPY');
                                        setPrintDropdown(null);
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('invoiceList.printCopy')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <button className="text-blue-600 hover:text-blue-900">
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => {
                                setInvoiceToDelete(invoice);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('invoiceList.previous')}
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      {t('invoiceList.next')}
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                                              <p className="text-sm text-gray-700">
                          {t('invoiceList.showingResults', { 
                            start: (currentPage - 1) * 10 + 1, 
                            end: Math.min(currentPage * 10, total), 
                            total: total 
                          })}
                        </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('invoiceList.previous')}
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('invoiceList.next')}
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">{t('invoiceList.deleteInvoice')}</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {t('invoiceList.deleteInvoiceConfirmation', { invoiceNumber: invoiceToDelete?.invoiceNumber })}
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setInvoiceToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  {t('invoiceList.cancel')}
                </button>
                <button
                  onClick={() => handleDelete(invoiceToDelete._id)}
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t('invoiceList.deleting') : t('invoiceList.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList; 